import { BADGES_BY_ID } from '../game/badges';
import { activityTitle } from '../lib/format';
import type { Locale, Messages, UnitSystem } from './index';
import { hu } from './messages/hu';

export interface NotificationCopy {
  type: string;
  title: string;
  body: string;
  data?: Record<string, string>;
}

export interface NotificationActivity {
  title: string | null;
  type: 'run' | 'walk' | 'ride';
  startedAt: number;
  endedAt?: number;
  durationS?: number;
}

/** Read parameters from the exact templates used before semantic notification data existed. */
function legacyNotificationData(item: NotificationCopy): Record<string, string> | null {
  const data: Record<string, string> = { ...item.data };
  const matchTitle = (pattern: RegExp) => item.title.match(pattern);
  const matchBody = (pattern: RegExp) => item.body.match(pattern);
  const numeric = (text: string | undefined) => text && /^(?:\d+|\d{1,3}(?:[ \u00a0\u202f]\d{3})+)(?:[.,]\d+)?$/.test(text)
    ? text.replace(/[ \u00a0\u202f]/g, '').replace(',', '.') : 'NaN';
  const gain = (body: string): boolean => {
    data.gp = '0';
    data.areaM2 = '0';
    if (!body) return true;
    const parts = body.split(' · ');
    return parts.every(part => {
      const area = part.match(/^\+([\d \u00a0\u202f]+(?:[.,]\d+)?) km² grund$/);
      const gp = part.match(/^\+([\d \u00a0\u202f]+) GP$/);
      if (area) data.areaM2 = String(Number(numeric(area[1])) * 1_000_000);
      else if (gp) data.gp = numeric(gp[1]);
      else return false;
      return true;
    });
  };
  let match: RegExpMatchArray | null;
  switch (item.type) {
    case 'followed_activity':
    case 'activity_liked':
    case 'activity_commented':
    case 'comment_replied':
    case 'mentioned': {
      const patterns: Record<string, RegExp> = {
        followed_activity: /^(.+) új aktivitást rögzített$/,
        activity_liked: /^(.+) kedvelte az aktivitásodat$/,
        activity_commented: /^(.+) hozzászólt az aktivitásodhoz$/,
        comment_replied: /^(.+) válaszolt a hozzászólásodra$/,
        mentioned: /^(.+) megemlített egy hozzászólásban$/,
      };
      match = matchTitle(patterns[item.type]!);
      if (!match) return null;
      data.actorName = match[1]!;
      data.preview = item.body;
      // A legacy title alone cannot prove whether the activity name was manually entered.
      data.activityTitle = data.activityTitle ?? item.body;
      break;
    }
    case 'new_follower':
    case 'follow_request':
      match = matchBody(item.type === 'new_follower'
        ? /^(.+) követni kezdett téged\.$/ : /^(.+) követni szeretne téged\.$/);
      if (!match) return null;
      data.actorName = match[1]!;
      break;
    case 'territory_stolen':
      match = matchBody(/^(.+) elvett tőled ([\d \u00a0\u202f]+(?:[.,]\d+)?) km² területet \(([\d \u00a0\u202f]+) cellát\)\.$/);
      if (!match) return null;
      data.actorName = match[1]!;
      data.areaM2 = String(Number(numeric(match[2])) * 1_000_000);
      data.cellCount = numeric(match[3]);
      data.rival = item.title === 'Egy riválisod megtámadta a grundod!' ? '1' : '0';
      break;
    case 'territory_defended':
      match = matchBody(/^([\d \u00a0\u202f]+) cellád védelme 1-gyel csökkent, de megtartottad a grundod\.$/);
      if (!match) return null;
      data.cellCount = numeric(match[1]);
      break;
    case 'badge_awarded': {
      match = matchBody(/^(.+) — \+?([\d \u00a0\u202f]+) GP$/);
      if (!match) return null;
      const badge = BADGES_BY_ID.get(data.badgeId ?? '') ??
        [...BADGES_BY_ID.values()].find(def => def.name === match?.[1]);
      if (!badge) return null;
      data.badgeId = badge.id;
      data.gp = numeric(match[2]);
      break;
    }
    case 'banda_membership':
      match = matchBody(/^(.+) (belépett a bandába|kilépett a bandából)\.$/);
      if (match) {
        data.crewName = item.title;
        data.actorName = match[1]!;
        data.event = match[2] === 'belépett a bandába' ? 'joined' : 'left';
      } else {
        match = matchBody(/^Eltávolítottak a\(z\) „(.+)” bandából\.$/);
        if (!match) return null;
        data.crewName = match[1]!;
        data.event = 'removed';
      }
      break;
    case 'banda_post':
      match = matchBody(/^(.+) új posztot írt a hírfolyamba\.$/);
      if (!match) return null;
      data.actorName = match[1]!;
      data.crewName = item.title;
      break;
    case 'banda_invited':
      match = matchBody(/^(.+) meghívott a\(z\) „(.+)” bandába\.$/);
      if (!match) return null;
      data.actorName = match[1]!;
      data.crewName = match[2]!;
      break;
    case 'banda_daily':
      match = matchTitle(/^(.+) — mai mérleg$/);
      if (!match) return null;
      data.crewName = match[1]!;
      match = matchBody(/^([\d \u00a0\u202f]+(?:[.,]\d+)?) km² terület és ([\d \u00a0\u202f]+) GP gyűlt össze ma\.$/);
      if (!match) return null;
      data.areaM2 = String(Number(numeric(match[1])) * 1_000_000);
      data.gp = numeric(match[2]);
      break;
    case 'gp_daily':
      match = matchBody(/^\+([\d \u00a0\u202f]+) GP a tartott grundodért\.$/);
      if (!match) return null;
      data.gp = numeric(match[1]);
      break;
    case 'gp_activity':
      if (item.title !== 'Aktivitás összegzés' || !item.body || !gain(item.body)) return null;
      break;
    case 'activity_saved': {
      if (item.title !== 'Kész a mentés') return null;
      if (data.activityTitle) {
        const prefix = `${data.activityTitle} · `;
        if (item.body !== data.activityTitle && !item.body.startsWith(prefix)) return null;
        if (!gain(item.body === data.activityTitle ? '' : item.body.slice(prefix.length))) return null;
        break;
      }
      const parts = item.body.split(' · ');
      const gains: string[] = [];
      while (parts.length > 1 && /^\+[\d \u00a0\u202f]+(?:[.,]\d+)? (?:km² grund|GP)$/.test(parts.at(-1) ?? '')) {
        gains.unshift(parts.pop()!);
      }
      if (!gain(gains.join(' · '))) return null;
      data.activityTitle = data.activityTitle ?? parts.join(' · ');
      break;
    }
    case 'activity_reviewed':
      if (item.body === 'Az ellenőrzés lezárult: ez az aktivitás nem kap területet és pontot.') data.approved = '0';
      else {
        const prefix = 'Az ellenőrzés lezárult, jóváírtuk.';
        if (item.body !== prefix && !item.body.startsWith(`${prefix} · `)) return null;
        if (!gain(item.body === prefix ? '' : item.body.slice(prefix.length + 3))) return null;
        data.approved = '1';
      }
      break;
    case 'report_resolved':
      if (item.body === 'Megvizsgáltuk a bejelentésedet, és intézkedtünk.') data.upheld = '1';
      else if (item.body === 'Megvizsgáltuk a bejelentésedet — szabályszegést nem találtunk.') data.upheld = '0';
      else return null;
      break;
    case 'streak_reminder':
      if (item.body === 'Tegyél meg ma egy sétát, hogy a sorozatod folytatódjon.') data.days = '1';
      else {
        match = matchBody(/^(\d+) napja tart a sorozatod\. Egy rövid séta ma is elég a folytatásához\.$/);
        if (!match) return null;
        data.days = match[1]!;
      }
      break;
    case 'personal_record': {
      const distance = matchTitle(/^Új egyéni rekord: (\d+(?:[.,]\d+)?) km$/);
      match = matchBody(/^(\d+):([0-5]\d) — (\d+) másodperccel jobb a korábbi (futó|sétás|bringás) legjobbnál \((\d+):([0-5]\d)\)\.$/);
      if (!distance || !match) return null;
      data.km = numeric(distance[1]);
      data.timeS = String(Number(match[1]) * 60 + Number(match[2]));
      data.previousS = String(Number(match[5]) * 60 + Number(match[6]));
      data.sport = match[4] === 'futó' ? 'run' : match[4] === 'sétás' ? 'walk' : 'ride';
      break;
    }
    case 'banda_chat_mention':
      match = matchTitle(/^(.+) (válaszolt neked|megemlített) a\(z\) (.+) chatjében$/);
      if (!match) return null;
      data.actorName = match[1]!;
      data.kind = match[2] === 'válaszolt neked' ? 'reply' : 'mention';
      data.crewName = match[3]!;
      data.preview = item.body;
      break;
    case 'message_request':
      match = matchBody(/^(.+) üzenetet küldött neked\.$/);
      if (!match) return null;
      data.actorName = match[1]!;
      break;
    case 'modifier_started':
      if (item.title !== 'Aktív akció indult') return null;
      data.defaultReason = item.body === 'Új akció' ? '1' : '0';
      break;
    // These legacy push bodies contain only user-authored text and names.
    case 'message': case 'banda_chat_message': default: return null;
  }
  return data;
}

/** Render semantic events and recognized historical system templates in the viewer's language. */
export function notificationText(
  item: NotificationCopy,
  messages: Messages,
  locale: Locale,
  units: UnitSystem = 'metric',
  activity?: NotificationActivity,
): { title: string; body: string } {
  const fallback = { title: item.title, body: item.body };
  if (locale === 'hu' && (units === 'metric' || !['badge_awarded', 'personal_record'].includes(item.type))) return fallback;
  const data = item.data?.textVersion === '1' ? item.data
    : item.data?.textVersion == null ? legacyNotificationData(item) : null;
  if (!data) return fallback;
  // Reuse already-loaded activity metadata. Never infer an automatic title from its text alone.
  if (item.data?.textVersion == null && activity && !activity.title &&
    ['followed_activity', 'activity_liked', 'activity_saved'].includes(item.type)) {
    const durationS = activity.durationS ?? 0;
    const endedAt = activity.endedAt ?? activity.startedAt;
    if ([activity.startedAt, endedAt, durationS].every(Number.isFinite) &&
      data.activityTitle === activityTitle(activity.type, activity.startedAt, durationS,
        endedAt, 'Europe/Budapest', hu.activity.autoTitle)) {
      data.automaticTitle = '1';
      data.activityType = activity.type;
      data.activityStartedAt = String(activity.startedAt);
      data.activityEndedAt = String(endedAt);
      data.activityDurationS = String(durationS);
    }
  }
  const t = messages.notificationText;
  const number = (value: number, digits = 0) => new Intl.NumberFormat(locale === 'hu' ? 'hu-HU' : 'en-US', {
    useGrouping: 'always' as unknown as boolean, maximumFractionDigits: digits,
  }).format(value);
  const area = (m2: number) => `${new Intl.NumberFormat(locale === 'hu' ? 'hu-HU' : 'en-US', {
    minimumFractionDigits: 3, maximumFractionDigits: 3,
  }).format(m2 / 1_000_000)} km²`;
  const value = (key: string) => data[key]?.trim() ? Number(data[key]) : NaN;
  const result = (title: string, body: string) => ({ title, body });
  const actor = data.actorName;
  const crew = data.crewName;
  const preview = data.preview ?? item.body;
  const activityName = () => {
    const start = value('activityStartedAt'), end = value('activityEndedAt'), duration = value('activityDurationS');
    const type = data.activityType;
    if (data.automaticTitle === '1' && (type === 'run' || type === 'walk' || type === 'ride') &&
      [start, end, duration].every(Number.isFinite)) {
      return activityTitle(type, start, duration, end, 'Europe/Budapest', messages.activity.autoTitle);
    }
    return data.activityTitle ?? item.body;
  };
  const gain = () => {
    const gp = value('gp'), m2 = value('areaM2');
    if (![gp, m2].every(Number.isFinite)) return null;
    return [m2 > 0 ? `+${area(m2)} ${t.grund}` : '', gp > 0 ? `+${number(Math.round(gp))} GP` : ''].filter(Boolean);
  };
  switch (item.type) {
    case 'modifier_started': return result(t.modifier, data.defaultReason === '1' ? t.newBoost : item.body);
    case 'badge_awarded': {
      const def = BADGES_BY_ID.get(data.badgeId ?? '');
      const gp = value('gp');
      if (!def || !Number.isFinite(gp)) return fallback;
      const b = messages.badges;
      const count = Number(def.id.split('_').at(-1));
      const distance = `${number(units === 'imperial' ? count / 1.609344 : count, 2)} ${units === 'imperial' ? 'mi' : 'km'}`;
      let name = def.name;
      switch (def.ladder) {
        case null: name = b.unique[def.id as keyof typeof b.unique]?.name ?? name; break;
        case 'distance_foot': case 'distance_bike': name = b.distanceName(b.ladders[def.ladder], distance); break;
        case 'territory': name = b.territoryName(area(count)); break;
        case 'conqueror': name = b.conquerorName(count); break;
        case 'streak': name = b.dayStreakName(count); break;
        case 'week_streak': name = b.weekStreakName(count); break;
        case 'loyalty': name = b.loyaltyName(b.loyaltyPeriods[def.ladderIndex] ?? b.days(count)); break;
        case 'explorer': name = b.explorerName(count); break;
        case 'comments': name = b.socialName(b.ladders.comments, b.comments(count)); break;
        case 'likes_given': case 'likes_received': name = b.socialName(b.ladders[def.ladder], b.hearts(count)); break;
      }
      return result(t.badge, `${name} — +${number(gp)} GP`);
    }
    case 'passport_country': {
      const iso = data.iso ?? '';
      if (!/^[A-Z]{2}$/.test(iso)) return fallback;
      const country = new Intl.DisplayNames([locale], { type: 'region' }).of(iso) ?? iso;
      return result(t.passport, t.passportBody(country));
    }
    case 'territory_stolen': {
      const m2 = value('areaM2'), count = value('cellCount');
      return actor && [m2, count].every(Number.isFinite)
        ? result(data.rival === '1' ? t.rivalAttack : t.stolen, t.stolenBody(actor, area(m2), number(count))) : fallback;
    }
    case 'territory_defended': {
      const count = value('cellCount');
      return Number.isFinite(count) ? result(t.defended, t.defendedBody(number(count))) : fallback;
    }
    case 'activity_commented': return actor ? result(t.commented(actor), preview) : fallback;
    case 'comment_replied': return actor ? result(t.replied(actor), preview) : fallback;
    case 'mentioned': return actor ? result(t.mentioned(actor), preview) : fallback;
    case 'activity_liked': {
      if (!actor) return fallback;
      const others = value('otherCount');
      return result(others > 0 ? t.likedMany(actor, others, number(others)) : t.liked(actor), activityName());
    }
    case 'followed_activity': return actor ? result(t.followedActivity(actor), activityName()) : fallback;
    case 'gp_activity': { const parts = gain(); return parts ? result(t.summary, parts.join(' · ')) : fallback; }
    case 'activity_saved': {
      const parts = gain();
      return parts && data.activityTitle ? result(t.saved, [activityName(), ...parts].join(' · ')) : fallback;
    }
    case 'activity_reviewed': {
      if (!['0', '1'].includes(data.approved ?? '')) return fallback;
      if (data.approved === '0') return result(t.reviewRejected, t.reviewNoCredit);
      const parts = gain();
      return parts ? result(t.reviewApproved, [t.reviewCredit, ...parts].join(' · ')) : fallback;
    }
    case 'gp_daily': { const gp = value('gp'); return Number.isFinite(gp) ? result(t.daily, t.dailyBody(number(Math.round(gp)))) : fallback; }
    case 'new_follower': return actor ? result(t.follower, t.newFollowerBody(actor)) : fallback;
    case 'follow_request': return actor ? result(t.followRequest, t.followRequestBody(actor)) : fallback;
    case 'report_resolved': return ['0', '1'].includes(data.upheld ?? '') ? result(t.report, data.upheld === '1' ? t.reportUpheld : t.reportDismissed) : fallback;
    case 'banda_invited': return actor && crew ? result(t.invite, t.inviteBody(actor, crew)) : fallback;
    case 'banda_membership':
      if (!crew) return fallback;
      if (data.event === 'removed') return result(t.removed, t.removedBody(crew));
      return actor && ['joined', 'left'].includes(data.event ?? '') ? result(crew, data.event === 'joined' ? t.joined(actor) : t.left(actor)) : fallback;
    case 'banda_post': return actor && crew ? result(crew, t.post(actor)) : fallback;
    case 'banda_daily': {
      const m2 = value('areaM2'), gp = value('gp');
      return crew && [m2, gp].every(Number.isFinite) ? result(t.crewDaily(crew), t.crewDailyBody(area(m2), number(gp))) : fallback;
    }
    case 'banda_chat_message': return actor && crew ? result(crew, `${actor}: ${preview}`) : fallback;
    case 'banda_chat_mention':
      return actor && crew && ['reply', 'mention'].includes(data.kind ?? '')
        ? result(data.kind === 'reply' ? t.chatReply(actor, crew) : t.chatMention(actor, crew), preview) : fallback;
    case 'streak_reminder': { const days = value('days'); return Number.isFinite(days) ? result(t.streak, days === 1 ? t.streakOne : t.streakBody(number(days))) : fallback; }
    case 'personal_record': {
      const km = value('km'), seconds = value('timeS'), previous = value('previousS');
      if (![km, seconds, previous].every(Number.isFinite) || !['run', 'walk', 'ride'].includes(data.sport ?? '')) return fallback;
      const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;
      const distance = `${number(units === 'imperial' ? km / 1.609344 : km, 2)} ${units === 'imperial' ? 'mi' : 'km'}`;
      const sport = data.sport === 'ride' ? t.rideRecord : data.sport === 'walk' ? t.walkRecord : t.runRecord;
      return result(t.record(distance), t.recordBody(mmss(seconds), number(Math.round(previous - seconds)), sport, mmss(previous)));
    }
    case 'message': return actor ? result(actor, preview || (data.hasImage === '1' ? t.image : '')) : fallback;
    case 'message_request': return actor ? result(t.messageRequest, t.requestBody(actor)) : fallback;
    default: return fallback;
  }
}
